/**
 * ¿Se pudo medir esta familia en ESTE período? La comprobación empírica.
 *
 * ## El agujero que cierra
 *
 * `sampling-quality.ts` ya contesta una pregunta parecida, pero la contesta
 * con física: «¿es posible que un vehículo haga lo que el umbral exige entre
 * dos puntos?». Eso deja pasar un caso que en producción resultó ser el más
 * común de todos.
 *
 * El 29/09/2026, con el perfil `LOW` v15 activo, un equipo de 10 s salía así:
 *
 * ```
 * DriveScore 73,8 · «Bueno»  —  «Los cuatro componentes se pudieron medir.»
 *   Velocidad alta operativa   Crítico · 25     166 eventos   35 %
 *   Frenadas bruscas           Bajo    · 100      0 eventos   25 %
 *   Aceleraciones bruscas      Bajo    · 100      0 eventos   20 %
 *   Giros de riesgo            Bajo    · 100     32 eventos   20 %
 * ```
 *
 * El umbral de aceleración era 1,15 m/s². A 10 s eso pide variar 41,4 km/h
 * entre dos puntos: un carro **puede** hacerlo, así que la comprobación física
 * decía `reachable: true`. Pero ese vehículo, en 3.058 km, **nunca** lo hizo —
 * su máximo observado quedaba muy por debajo. El detector no podía disparar, y
 * sus dos ceros se publicaron como **100 sobre 100, con el 45 % del peso**.
 *
 * El resultado es peor que un puntaje bajo: es un puntaje **alto y falso**. Un
 * vehículo que solo podía perder puntos en un detector de cuatro, y cuyo único
 * detector medible estaba en Crítico, salía «Bueno» hacia una aseguradora.
 *
 * Es el ADR-007 otra vez —un cero que significa «no se pudo medir» no se
 * presenta como «no ocurrió»— entrando por una puerta que nadie había mirado:
 * no la de lo físicamente imposible, sino la de lo que **no ocurre en la
 * muestra que se está puntuando**.
 *
 * ## La regla
 *
 * Por familia, en este orden:
 *
 * 1. **Hubo episodios** → se midió. Es prueba directa, y gana a cualquier
 *    estimación. Esta parte ya existía y se conserva tal cual.
 * 2. **Hubo al menos un par en el que el valor superó el umbral que le tocaba
 *    a ese par** → el detector tuvo su oportunidad; si no disparó, es una
 *    medición legítima de «no ocurrió».
 * 3. **No hubo ninguno** → **no se midió**. No es una estimación: en este
 *    período, ese detector no tuvo ni una sola oportunidad de dispararse. Sin
 *    ninguna medición se llega aquí igual, y es lo correcto: de una familia de
 *    la que no se observó nada tampoco se puede decir que se midió.
 * 4. Cuando no se puede afirmar —la aceleración lateral abre un segundo camino
 *    al giro, o falta el intervalo emparejado para traducir una compuerta— el
 *    módulo **se abstiene** y deja el criterio físico de siempre.
 *
 * «El umbral que le tocaba a ese par» no es el umbral suelto: cada detector
 * exige varias condiciones a la vez, y las secundarias están en otra unidad.
 * Un giro pide también `minimumHeadingChangeDegrees`, que a 10 s son 3,5 °/s si
 * el mínimo es 35°; una aceleración pide `minimumSpeedChangeKmh`. Se traducen
 * con el intervalo **de ese par**, no con un percentil del conjunto: ver
 * {@link FamilyObservation}.
 *
 * No hay percentiles ni muestra mínima, y es a propósito. La pregunta «¿hubo
 * algún par que llegara?» es un hecho comprobable par a par; cualquier resumen
 * estadístico previo —un máximo, un p90— mezcla pares distintos y ya demostró
 * dos veces que deja volver el informe falso.
 *
 * ## El cero de un vehículo suave con un umbral común (ADR-009)
 *
 * La regla 3 nació del caso de arriba: un umbral que ESE vehículo nunca
 * alcanzaba, y un cero publicado como 100. Pero no distingue dos ceros que no se
 * parecen en nada:
 *
 *  - un umbral por encima de todo lo que la flota hace: nadie lo alcanza, y el
 *    cero no dice nada del vehículo — es el caso del 29/09;
 *  - un umbral común que la mayoría de la cadencia sí alcanza, y un vehículo que
 *    en 300 km o más no lo alcanzó nunca: ese cero dice que el vehículo no hizo
 *    esa maniobra.
 *
 * Desde el ADR-009, el segundo se cuenta como medido (`within_regime_range`),
 * con cinco condiciones a la vez: el perfil trae la cobertura de flota con que
 * se guardó (`fleetCoverage`); en esa flota la familia pudo dispararse en al
 * menos la mitad de los vehículos; este vehículo recorrió al menos
 * {@link MINIMUM_FLEET_DISTANCE_KM} km elegibles; la cadencia lo permite
 * físicamente; y hubo mediciones de la familia, alguna de las cuales pasó la
 * compuerta secundaria. La velocidad alta queda fuera:
 * su umbral es una política, y no llegar nunca a 80 km/h dice dónde anda el
 * vehículo, no cómo.
 *
 * El cero así medido se dice en el informe, con las cifras de la flota
 * ({@link explainRegimeZeros}): un 100 sin explicación era justo el defecto del
 * 29/09.
 *
 * ## Lo que este módulo NO hace
 *
 * No cambia ningún umbral, no cuenta episodios y no toca el análisis. Solo
 * mira lo que el análisis ya midió y responde si la familia tuvo oportunidad.
 * Y no arregla la causa de fondo —que un solo perfil por cadencia no sirve a
 * vehículos de entornos distintos—: hace que el informe deje de mentir
 * mientras esa causa se arregla.
 */

import type { CandidateMeasurements } from "../pipeline/driving-analysis-v5.ts";
import type { AnalysisProfile } from "../pipeline/profiles.ts";
import type { DetectorReachability } from "../pipeline/sampling-quality.ts";
import type { ScoreComponent } from "./drivescore-v1.ts";

/**
 * Kilómetros elegibles mínimos para que un cero pueda contarse como medido por
 * la referencia de la cadencia, y para que un vehículo cuente en la verificación
 * de flota. Es la misma barra con que se simuló el umbral común el 06/10/2026.
 */
export const MINIMUM_FLEET_DISTANCE_KM = 300;

/** Parte de la cadencia que tiene que alcanzar un umbral para llamarlo normal. */
export const MINIMUM_FIRING_SHARE = 0.5;

export type MeasurabilityReason =
  /** Disparó. Prueba directa, gana a cualquier estimación. */
  | "episodes_found"
  /** Algún par llegó al umbral: pudo disparar aunque no disparara. */
  | "within_observed_range"
  /** Ningún par llegó al umbral: no tuvo ni una oportunidad. */
  | "above_observed_range"
  /** Físicamente imposible a esta cadencia (la comprobación de siempre). */
  | "unreachable_at_cadence"
  /** Muestra insuficiente para afirmar nada: se usó el criterio físico. */
  | "sample_too_small"
  /**
   * Ningún par de ESTE vehículo llegó al umbral, pero el umbral es común de su
   * cadencia y la mayoría de la cadencia sí lo alcanza (ADR-009): el cero es
   * una medición.
   */
  | "within_regime_range";

export type FamilyMeasurability = {
  component: ScoreComponent;
  detector: DetectorReachability["detector"];
  measured: boolean;
  reason: MeasurabilityReason;
  /** Umbral aplicado, en la unidad de la familia. Siempre positivo. */
  threshold: number | null;
  /** Lo más alto que este vehículo hizo en el período, misma unidad. */
  observedMax: number | null;
  /** Cuántas mediciones respaldan ese máximo. */
  observedCount: number;
  episodes: number;
  /**
   * Con `within_regime_range`: en cuántos vehículos de la cadencia pudo
   * dispararse la familia, de cuántos. Es lo que el informe imprime para que el
   * cero no quede como un 100 sin explicación.
   */
  regimeReference?: { firing: number; vehicles: number };
  /** La cadencia pasa del techo de maniobras (`sampling-quality.ts`). */
  beyondCadenceCeiling?: boolean;
};

/**
 * Qué compuerta secundaria gobierna una familia, además de su umbral.
 *
 * Se nombra la perilla y no solo su valor porque el aviso tiene que mandar a
 * mover la perilla correcta: «ningún par llegó a 17,9 °/s» sin decir que esos
 * 17,9 los pide el cambio de rumbo mínimo manda a bajar el umbral angular, que
 * no es el que está bloqueando.
 */
export type GateKind = "speed_change_kmh" | "heading_change_degrees";

/**
 * ¿Habría podido disparar esta familia, con estos umbrales y esta muestra?
 *
 * Es el paso 2 de la regla, suelto y sin episodios ni alcance físico, para que
 * quien tenga que hacer la MISMA pregunta antes de activar un perfil no la
 * vuelva a escribir: ver {@link assessFiringChance}.
 */
export type FamilyFiringChance = {
  component: ScoreComponent;
  /**
   * `true` si al menos un par llegó al umbral que le tocaba; `false` si ninguno;
   * `null` cuando no se puede afirmar (familia en abstención, o compuerta que no
   * se puede traducir por falta del intervalo emparejado).
   */
  couldFire: boolean | null;
  /** Umbral configurado, en la unidad de la familia. `null` en abstención. */
  threshold: number | null;
  /** Lo más alto que este vehículo hizo en el período, misma unidad. */
  observedMax: number | null;
  observedCount: number;
  /**
   * Lo que el detector exigía **en el par que produjo ese máximo**: el umbral, o
   * la compuerta secundaria traducida con el intervalo de ESE par cuando pide
   * más.
   *
   * Es el par del máximo y no «el que estuvo más cerca» a propósito. El informe
   * ya publica ese máximo, así que las dos cifras describen el mismo par y el
   * lector no tiene que preguntarse cuál de dos máximos es el real. La
   * alternativa descartada —el par que maximiza valor/exigencia— sería más útil
   * para calibrar y por eso queda como lo que el Laboratorio muestra tramo a
   * tramo, no como el número del aviso.
   */
  requirementAtObservedMax: number | null;
  /** Qué compuerta secundaria tiene esta familia, si tiene alguna. */
  gateKind: GateKind | null;
  /** Esa compuerta tal como está configurada, en su propia unidad. */
  gateValue: number | null;
  /**
   * `true` cuando en el par del máximo la compuerta pide MÁS que el umbral: es
   * ella la que manda, y bajar el umbral no cambiaría nada.
   */
  gateBinds: boolean;
  /** Intervalo del par del máximo, s. `null` si la familia no depende de él. */
  intervalAtObservedMax: number | null;
  /**
   * ¿Algún par pasó la compuerta secundaria, **sin mirar el umbral**?
   *
   * `null` cuando no hay compuerta o no se puede traducir. Un `false` es más
   * fuerte que `couldFire === false`: la compuerta es una condición aparte que
   * el detector exige siempre, así que si ningún par la pasa la familia queda
   * muda **incluso cuando el umbral no gobierna** —el caso de la aceleración
   * lateral encendida, donde `couldFire` se abstiene—.
   */
  gateAlone: boolean | null;
  /**
   * Giro por debajo de `minimumReferenceSpeedKmh`: no cuenta nunca, por muy
   * holgado que sea el umbral angular. Vive aparte de `couldFire` porque está en
   * otra magnitud y porque tiene precedencia sobre la abstención.
   */
  belowReferenceSpeed: boolean;
  /**
   * La velocidad más alta con la que se tomó una curva en el período, km/h.
   * Solo tiene sentido en `giros`; en las demás familias es `null`.
   */
  referenceSpeedMax: number | null;
};

/** Unidad de cada familia, para poder redactar el aviso sin repetirla. */
export const FAMILY_UNITS: Record<ScoreComponent, string> = {
  velocidadAlta: "km/h",
  frenadas: "m/s²",
  aceleraciones: "m/s²",
  giros: "°/s",
};

const round3 = (value: number) => Math.round(value * 1000) / 1000;

const maxOf = (values: number[]): number | null =>
  values.length ? round3(values.reduce((best, value) => (value > best ? value : best), values[0]!)) : null;

/**
 * Lo observado por familia, **emparejado con el intervalo de su propio par**.
 *
 * Reusa `measureCandidateValues` en vez de recorrer los tramos otra vez. Este
 * repositorio ya pagó dos veces el precio de escribir la misma regla en dos
 * sitios —`applyThresholds` en septiembre, `inspectDriving` después—, así que
 * aquí no se vuelve a medir nada: se lee lo que el laboratorio ya mide.
 *
 * ## Por qué el intervalo viaja junto a cada valor
 *
 * Las compuertas secundarias del detector están en otra unidad que el umbral
 * principal, y la conversión depende del intervalo: `minimumSpeedChangeKmh` es
 * un mínimo de m/s² solo después de dividir por Δt, y `minimumHeadingChangeDegrees`
 * es un mínimo de °/s por la misma razón.
 *
 * Una versión anterior tradujo esas compuertas con **un** intervalo para toda la
 * muestra (el p90) y comparó el resultado contra el **máximo de todos** los
 * pares. Una revisión adversarial demostró que eso vuelve a publicar el informe
 * falso: con un equipo de 10 s de mediana y un 14 % de tramos de 30 s, el p90
 * sale 20 s, el umbral de giro se ablanda a 35/20 = 1,75 °/s y un vehículo cuyo
 * máximo real era 2 °/s —que a su cadencia necesitaba 3,5— se declaraba medido
 * y puntuaba 100. Mezclar el intervalo de unos pares con el máximo de otros no
 * describe ningún par que haya existido.
 *
 * Emparejado, la pregunta deja de necesitar percentiles: **¿hubo al menos un par
 * en el que el valor superó el umbral que le tocaba a ese par?** Eso es un hecho
 * del período, comprobable par a par, sin estimar nada.
 */
type FamilyObservation = {
  /** Valores observados, siempre positivos y en la unidad de la familia. */
  values: number[];
  /**
   * Intervalo de cada valor, alineado índice a índice. `null` cuando la familia
   * no depende del intervalo (velocidad) o cuando no llegó emparejado.
   */
  intervals: number[] | null;
  /** Umbral configurado: el número que el analista ve en el Laboratorio. */
  base: number;
  /**
   * Compuerta secundaria en unidad·segundo, para dividir por el intervalo de
   * cada par. Cero cuando no hay ninguna.
   */
  gate: number;
  /** Qué perilla es esa compuerta, para poder nombrarla. */
  gateKind: GateKind | null;
  /** Esa misma perilla en la unidad en que está escrita en el perfil. */
  gateValue: number;
  /** `true` cuando no se puede afirmar que un solo número gobierne la familia. */
  abstain: boolean;
};

function observeFamilies(
  measurements: CandidateMeasurements,
  profile: AnalysisProfile,
): Record<ScoreComponent, FamilyObservation> {
  const { longitudinal, turns, elevatedSpeed } = profile;

  // `longitudinalMps2` y `midpointIntervalSeconds` sí salen emparejados del
  // mismo bucle, pero filtrar por signo rompería el emparejamiento: hay que
  // recorrer por índice y quedarse con las dos cosas a la vez.
  const intervals = measurements.midpointIntervalSeconds;
  const emparejado = intervals.length === measurements.longitudinalMps2.length;
  const accel: number[] = [];
  const accelDt: number[] = [];
  const brake: number[] = [];
  const brakeDt: number[] = [];
  measurements.longitudinalMps2.forEach((value, index) => {
    const dt = emparejado ? intervals[index]! : null;
    if (value > 0) {
      accel.push(value);
      if (dt !== null) accelDt.push(dt);
    } else if (value < 0) {
      brake.push(Math.abs(value));
      if (dt !== null) brakeDt.push(dt);
    }
  });

  // `turnIntervalSeconds` es el único que viene alineado con la velocidad
  // angular; `midpointIntervalSeconds` no sirve aquí y usarlo sería el mismo
  // error de mezclar dos subconjuntos. Si no llegó —respuesta de una versión
  // anterior del contrato— la familia se juzga sin la compuerta, no con una
  // inventada.
  const turnDt = measurements.turnIntervalSeconds ?? [];
  const turnsPaired = turnDt.length === measurements.yawDegreesPerSecond.length;

  // Δv (km/h) = a (m/s²) × Δt (s) × 3,6 ⇒ la compuerta de Δv es un mínimo de a.
  const speedChangeGate = Math.max(0, longitudinal.minimumSpeedChangeKmh) / 3.6;
  // Cambio de rumbo (°) = velocidad angular (°/s) × Δt (s).
  const headingGate = Math.max(0, turns.minimumHeadingChangeDegrees);

  return {
    aceleraciones: {
      values: accel,
      intervals: emparejado ? accelDt : null,
      base: longitudinal.accelerationThresholdMps2,
      gate: speedChangeGate,
      gateKind: speedChangeGate > 0 ? "speed_change_kmh" : null,
      gateValue: longitudinal.minimumSpeedChangeKmh,
      abstain: false,
    },
    frenadas: {
      values: brake,
      intervals: emparejado ? brakeDt : null,
      base: Math.abs(longitudinal.brakingThresholdMps2),
      gate: speedChangeGate,
      gateKind: speedChangeGate > 0 ? "speed_change_kmh" : null,
      gateValue: longitudinal.minimumSpeedChangeKmh,
      abstain: false,
    },
    giros: {
      values: measurements.yawDegreesPerSecond,
      intervals: turnsPaired ? turnDt : null,
      base: turns.minimumYawRateDegreesPerSecond,
      gate: headingGate,
      gateKind: headingGate > 0 ? "heading_change_degrees" : null,
      gateValue: turns.minimumHeadingChangeDegrees,
      // Con la aceleración lateral encendida hay un segundo camino para
      // disparar un giro, así que la velocidad angular deja de gobernar la
      // familia y no se puede concluir nada de ella.
      abstain: turns.lateralAccelerationMps2 > 0,
    },
    velocidadAlta: {
      values: measurements.segmentSpeedKmh,
      // No depende del intervalo: es km/h contra km/h. Las compuertas de tramo
      // solo pueden quitar episodios, nunca añadirlos, así que el límite sigue
      // siendo una cota superior válida para afirmar imposibilidad.
      intervals: null,
      base: elevatedSpeed.operationalSpeedLimitKmh,
      gate: 0,
      gateKind: null,
      gateValue: 0,
      abstain: false,
    },
  };
}

/**
 * ¿Hubo al menos un par donde el valor superó el umbral que le tocaba?
 *
 * `null` cuando no se puede afirmar: familia en abstención, o con una compuerta
 * secundaria que no se puede traducir porque falta el intervalo emparejado.
 */
function couldFire(observation: FamilyObservation): boolean | null {
  if (observation.abstain) return null;
  if (!observation.values.length) return false;
  if (observation.gate > 0 && observation.intervals === null) return null;

  return observation.values.some((value, index) => value >= requirementAt(observation, index));
}

/**
 * Lo que el detector exige en el par `index`: el umbral, o la compuerta
 * secundaria traducida con el intervalo de ESE par si pide más.
 *
 * Suelta y no en línea dentro de `couldFire` porque el aviso tiene que poder
 * publicar el mismo número que el veredicto usó. Calcularlo dos veces es cómo
 * empiezan las dos versiones de una regla.
 */
function requirementAt(observation: FamilyObservation, index: number): number {
  const dt = observation.intervals?.[index];
  const fromGate = observation.gate > 0 && dt ? observation.gate / dt : 0;
  return Math.max(observation.base, fromGate);
}

/** Índice del valor más alto, o `-1` si no hay ninguno. */
function indexOfMax(values: number[]): number {
  let best = -1;
  values.forEach((value, index) => {
    if (best < 0 || value > values[best]!) best = index;
  });
  return best;
}

/**
 * La pregunta par a par, por familia, sin episodios ni alcance físico.
 *
 * Existe para que la guarda de calibración pueda hacer **la misma** pregunta que
 * el informe antes de que un perfil se active: *con estos umbrales y esta
 * muestra, ¿habría podido disparar cada familia?* Escribirla otra vez allá sería
 * la tercera copia de una regla que ya volvió dos veces por haberse duplicado, y
 * esta vez con el agravante de que las dos copias contestarían en momentos
 * distintos —una al guardar, otra al publicar— sobre el mismo período.
 *
 * `assessMeasurability` la consume y le añade lo que solo el informe sabe: los
 * episodios que sí se consolidaron y el alcance físico de la cadencia.
 */
export function assessFiringChance(
  measurements: CandidateMeasurements,
  profile: AnalysisProfile,
): Record<ScoreComponent, FamilyFiringChance> {
  const observations = observeFamilies(measurements, profile);

  const turnSpeeds = measurements.turnReferenceSpeedKmh;
  const turnSpeedMax = maxOf(turnSpeeds);
  const turnSpeedBlocks =
    turnSpeeds.length > 0 &&
    turnSpeedMax !== null &&
    profile.turns.minimumReferenceSpeedKmh > turnSpeedMax;

  const out = {} as Record<ScoreComponent, FamilyFiringChance>;
  for (const component of COMPONENT_ORDER) {
    const observation = observations[component];
    const top = indexOfMax(observation.values);
    const dt = top < 0 ? undefined : observation.intervals?.[top];
    const requirement = top < 0 ? null : round3(requirementAt(observation, top));
    const fromGate = observation.gate > 0 && dt ? observation.gate / dt : 0;

    // La compuerta por su cuenta: el detector la exige además del umbral, así
    // que un `false` aquí basta para afirmar que la familia queda muda.
    const intervals = observation.intervals;
    const gateAlone =
      observation.gate <= 0 || intervals === null
        ? null
        : observation.values.some((value, index) => {
            const each = intervals[index];
            return each ? value >= observation.gate / each : false;
          });

    out[component] = {
      component,
      couldFire: couldFire(observation),
      gateAlone,
      threshold: observation.abstain ? null : observation.base,
      observedMax: maxOf(observation.values),
      observedCount: observation.values.length,
      requirementAtObservedMax: requirement,
      gateKind: observation.gateKind,
      gateValue: observation.gateKind === null ? null : observation.gateValue,
      // «Manda la compuerta» es que pida MÁS que el umbral en ese par. Empatada
      // con el umbral no manda: bajarla no cambiaría nada.
      gateBinds: observation.gateKind !== null && fromGate > observation.base,
      intervalAtObservedMax: dt ?? null,
      belowReferenceSpeed: component === "giros" && turnSpeedBlocks,
      referenceSpeedMax: component === "giros" ? turnSpeedMax : null,
    };
  }
  return out;
}


const DETECTOR_OF: Record<ScoreComponent, DetectorReachability["detector"]> = {
  velocidadAlta: "elevated_speed",
  frenadas: "braking",
  aceleraciones: "acceleration",
  giros: "turn",
};

export const COMPONENT_ORDER: ScoreComponent[] = [
  "velocidadAlta",
  "frenadas",
  "aceleraciones",
  "giros",
];

/**
 * Qué tipos de episodio alimentan cada familia del puntaje.
 *
 * Este mapa estaba escrito en la pantalla y otra vez en el informe descargable.
 * Vive aquí para que haya uno solo: un tipo nuevo de episodio que se olvide en
 * una de las dos copias es un componente que se declara «sin medición» en un
 * sitio y «medido» en el otro, con el mismo período y los mismos datos.
 */
export const EPISODE_TYPES_OF: Record<ScoreComponent, readonly string[]> = {
  velocidadAlta: ["elevated_speed_candidate"],
  frenadas: ["braking_candidate"],
  aceleraciones: ["acceleration_candidate"],
  giros: ["left_turn_candidate", "right_turn_candidate"],
};

/** Episodios consolidados por familia. La entrada de `assessMeasurability`. */
export function countEpisodesByFamily(
  episodes: ReadonlyArray<{ type: string }>,
): Record<ScoreComponent, number> {
  const counted = {} as Record<ScoreComponent, number>;
  for (const component of COMPONENT_ORDER) {
    const types = EPISODE_TYPES_OF[component];
    counted[component] = episodes.filter((episode) => types.includes(episode.type)).length;
  }
  return counted;
}

/**
 * ¿Respalda la flota de la cadencia el cero de esta familia? (ADR-009)
 *
 * `null` cuando no: entonces el cero sigue la regla de siempre.
 */
function regimeReferenceFor(
  component: ScoreComponent,
  profile: AnalysisProfile,
): { firing: number; vehicles: number } | null {
  // La velocidad alta es una política, no una maniobra: no llegar nunca al
  // límite dice por dónde anda el vehículo, no cómo maneja.
  if (component === "velocidadAlta") return null;
  const coverage = profile.fleetCoverage;
  if (!coverage || !(coverage.vehicles > 0)) return null;
  const firing = coverage.firing[component] ?? 0;
  if (firing < coverage.vehicles * MINIMUM_FIRING_SHARE) return null;
  return { firing, vehicles: coverage.vehicles };
}

export function assessMeasurability(input: {
  profile: AnalysisProfile;
  measurements: CandidateMeasurements;
  /** Alcance físico por detector, tal como lo calcula `describeSampling`. */
  detectors: DetectorReachability[];
  /** Episodios consolidados por familia, los mismos que van al puntaje. */
  episodes: Record<ScoreComponent, number>;
  /**
   * Kilómetros elegibles del período. Solo los usa la regla del cero de
   * cadencia (ADR-009); sin ellos esa regla no se aplica y todo sigue igual.
   */
  distanceKm?: number | null;
}): FamilyMeasurability[] {
  // La pregunta par a par se hace UNA vez, y es la misma que se hace la guarda
  // de calibración antes de activar el perfil: ver `assessFiringChance`.
  const chances = assessFiringChance(input.measurements, input.profile);

  return COMPONENT_ORDER.map((component) => {
    const detector = DETECTOR_OF[component];
    const episodes = input.episodes[component] ?? 0;
    const chance = chances[component];
    const base = {
      component,
      detector,
      // Se publica el umbral CONFIGURADO, el que el analista ve en el
      // Laboratorio. Publicar el efectivo imprimía en el informe un número que
      // nadie escribió y que nadie podía encontrar en pantalla; cuando la que
      // impide disparar es una compuerta secundaria, lo dice el texto con su
      // propio número.
      threshold: chance.threshold,
      observedMax: chance.observedMax,
      observedCount: chance.observedCount,
      episodes,
    };

    const reach = input.detectors.find((item) => item.detector === detector);

    // 0. Por encima del techo de cadencia lo que el motor marca no es una
    //    maniobra: ni un episodio encontrado prueba nada. Va antes que la
    //    regla 1 por eso mismo (ver `MANEUVER_CADENCE_CEILING_SECONDS`).
    if (reach?.beyondCadenceCeiling) {
      return {
        ...base,
        measured: false,
        reason: "unreachable_at_cadence" as const,
        beyondCadenceCeiling: true,
      };
    }

    // 1. Haber encontrado un episodio ES la prueba de que la familia se midió.
    //    Gana a cualquier estimación.
    if (episodes > 0) return { ...base, measured: true, reason: "episodes_found" as const };

    const physicallyReachable = reach?.reachable ?? true;

    // 2. Un giro por debajo de la velocidad mínima no cuenta nunca. Tiene
    //    precedencia sobre la abstención: con la aceleración lateral encendida
    //    tampoco hay giro que medir por debajo de esa velocidad.
    if (chance.belowReferenceSpeed) {
      return { ...base, measured: false, reason: "above_observed_range" as const };
    }

    // 3. ¿Hubo algún par en que el valor superara el umbral que le tocaba?
    const fired = chance.couldFire;

    // Abstención declarada: no se puede concluir, se deja el criterio físico.
    if (fired === null) {
      return {
        ...base,
        measured: physicallyReachable,
        reason: physicallyReachable
          ? ("within_observed_range" as const)
          : ("unreachable_at_cadence" as const),
      };
    }

    if (fired) {
      // Pudo disparar y no disparó. Eso sí es una medición: no ocurrió.
      return {
        ...base,
        measured: physicallyReachable,
        reason: physicallyReachable
          ? ("within_observed_range" as const)
          : ("unreachable_at_cadence" as const),
      };
    }

    // 4. Ni un solo par llegó al umbral. Antes de declararlo sin medición, la
    //    pregunta del ADR-009: ¿es un umbral común que la mayoría de la cadencia
    //    sí alcanza, sobre kilometraje suficiente? Entonces el cero es una
    //    medición de este vehículo, y se dice con las cifras de la flota.
    const reference = regimeReferenceFor(component, input.profile);
    if (
      reference &&
      physicallyReachable &&
      chance.observedCount > 0 &&
      chance.gateAlone !== false &&
      typeof input.distanceKm === "number" &&
      input.distanceKm >= MINIMUM_FLEET_DISTANCE_KM
    ) {
      return {
        ...base,
        measured: true,
        reason: "within_regime_range" as const,
        regimeReference: reference,
      };
    }

    //    Si no, el detector no tuvo una oportunidad, y un cero así no es
    //    «bien»: es «no se pudo medir» (ADR-007).
    //
    //    Sin ninguna medición se llega aquí igual, y es lo correcto: una familia
    //    de la que no se observó nada tampoco se midió. La versión anterior le
    //    daba `measured: true` con el motivo «muestra insuficiente» —un motivo
    //    que además no se imprime nunca, porque solo se redactan las familias no
    //    medidas—, y así un período entero sin mediciones publicaba DriveScore
    //    100 con lectura de riesgo «Bajo».
    return {
      ...base,
      measured: false,
      reason: chance.observedCount
        ? ("above_observed_range" as const)
        : ("sample_too_small" as const),
    };
  });
}

/**
 * La medibilidad de antes de este módulo: solo alcance físico y episodios.
 *
 * Existe para que un consumidor que reciba una respuesta **sin** el bloque de
 * medibilidad —una versión anterior del contrato— tenga un camino de código
 * igual al nuevo en vez de uno propio. No mejora nada y no pretende hacerlo:
 * reproduce el criterio viejo, y por eso nunca devuelve `above_observed_range`.
 *
 * La alternativa descartada era dejar cada consumidor con dos ramas —«si llegó,
 * úsalo; si no, calcula»—, que es exactamente la duplicación que este trabajo
 * vino a quitar. Con esto la degradación está escrita UNA vez y se ve.
 */
export function measurabilityFromReachability(input: {
  detectors: DetectorReachability[];
  episodes: Record<ScoreComponent, number>;
}): FamilyMeasurability[] {
  return COMPONENT_ORDER.map((component) => {
    const detector = DETECTOR_OF[component];
    const episodes = input.episodes[component] ?? 0;
    const reachable = input.detectors.find((item) => item.detector === detector)?.reachable ?? true;
    const measured = episodes > 0 || reachable;
    return {
      component,
      detector,
      measured,
      reason: episodes > 0 ? "episodes_found" : measured ? "within_observed_range" : "unreachable_at_cadence",
      threshold: null,
      observedMax: null,
      observedCount: 0,
      episodes,
    };
  });
}

/**
 * Número en castellano de Colombia: coma decimal y punto de miles.
 *
 * Estas líneas se imprimen tal cual en el informe que va al cliente y a la
 * aseguradora, donde el resto del documento ya escribe «73,8» y «3.058». Un
 * mismo PDF que dijera «el umbral de 1.15 m/s²» dos párrafos más arriba de
 * «DriveScore 73,8» no es un detalle de estilo: «1.15» en Colombia se lee
 * *mil ciento cincuenta*, y el número que sostiene el aviso es justamente el que
 * queda ambiguo.
 *
 * A mano y no con `toLocaleString("es-CO")` —que es lo que usa la guarda de
 * calibración, donde el texto no viaja a un PDF— porque este módulo alimenta un
 * informe que se declara puro: el mismo HTML byte a byte, lo genere el navegador
 * o el Worker. Con `Intl` el documento dependería de la tabla de locales de cada
 * runtime, que es una entrada que nadie firmó.
 */
function esCO(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "—";
  const [whole, fraction = ""] = value.toFixed(3).split(".");
  const grouped = whole!.replace(/\B(?=(\d{3})+(?!\d))/gu, ".");
  // Se recortan los ceros de la derecha en vez de fijar dos decimales: 1,15 es
  // el umbral que se configuró y 1,150 sugeriría una precisión que no se pidió.
  const trimmed = fraction.replace(/0+$/u, "");
  return trimmed ? `${grouped},${trimmed}` : grouped;
}

/**
 * Una línea por familia cuyo cero se contó como medido por la referencia de la
 * cadencia (ADR-009).
 *
 * Va en el informe al lado del puntaje, en los dos canales. Sin ella, un 100 en
 * aceleraciones con cero episodios se vería igual que el 100 falso del 29/09, y
 * la diferencia —la mayoría de la cadencia sí llega a ese umbral— es justo lo
 * que lo hace legítimo.
 */
export function explainRegimeZeros(
  families: FamilyMeasurability[],
  labels: Record<ScoreComponent, string>,
): string[] {
  return families
    .filter((family) => family.reason === "within_regime_range" && family.regimeReference)
    .map((family) => {
      const unit = FAMILY_UNITS[family.component];
      const reference = family.regimeReference!;
      return (
        `${labels[family.component]}: 0 episodios. Este vehículo no llegó al umbral común de su cadencia ` +
        `(${esCO(family.threshold)} ${unit}; lo más alto que hizo fue ${esCO(family.observedMax)} ${unit}), ` +
        `que sí alcanzaron ${esCO(reference.firing)} de ${esCO(reference.vehicles)} vehículos de esa ` +
        `cadencia con al menos ${esCO(MINIMUM_FLEET_DISTANCE_KM)} km cuando se calibró. Por eso el cero ` +
        `cuenta como medición: en este período no ocurrió.`
      );
    });
}

/** Una línea por familia no medida, para el aviso del informe. */
export function explainUnmeasured(families: FamilyMeasurability[], labels: Record<ScoreComponent, string>): string[] {
  return families
    .filter((family) => !family.measured)
    .map((family) => {
      const unit = FAMILY_UNITS[family.component];
      if (family.beyondCadenceCeiling) {
        return (
          `${labels[family.component]}: el equipo reporta tan espaciado que una maniobra entera cabe entre ` +
          `dos lecturas. Lo que el motor marque en esta familia no es una medición, y no entra al puntaje.`
        );
      }
      if (family.reason === "above_observed_range") {
        return (
          `${labels[family.component]}: el umbral de ${esCO(family.threshold)} ${unit} está por encima de ` +
          `lo más alto que este vehículo hizo en el período (${esCO(family.observedMax)} ${unit}), ` +
          `sobre ${esCO(family.observedCount)} mediciones. El detector no tuvo ninguna oportunidad de ` +
          `dispararse, así que su cero significa «no se pudo medir», no «no ocurrió».`
        );
      }
      return (
        `${labels[family.component]}: a la cadencia de reporte de este equipo, lo que el umbral ` +
        `exige entre dos puntos no es alcanzable. Su cero significa «no se pudo medir».`
      );
    });
}

/**
 * Título del aviso de medibilidad. Uno solo, y por eso vive aquí.
 *
 * La pantalla y el informe descargable publican el MISMO aviso sobre el mismo
 * período, y el ADR-007 exige que vaya arriba de las cifras en los dos. Tenerlo
 * escrito dos veces es la clase de copia que este módulo vino a quitar: la
 * primera divergencia no sería un número distinto sino un encabezado distinto
 * para el mismo hecho, que es más difícil de notar y hace el mismo daño.
 *
 * No nombra la causa a propósito. Las causas son por familia y pueden ser dos
 * distintas en el mismo informe; quien las redacta es {@link explainUnmeasured},
 * y un título que eligiera una de las dos mentiría sobre la otra.
 */
export const UNMEASURED_NOTICE_TITLE = "Parte de este período no se pudo medir" as const;

/**
 * Qué decir cuando el período no consolidó ni un solo episodio.
 *
 * ## El texto que contradecía al puntaje de la misma pantalla
 *
 * La pantalla del informe decidía esto con `detectorsBlindedBySampling`: si la
 * cadencia no había cegado a los detectores, imprimía «Los detectores sí eran
 * alcanzables con estos datos». Era una copia viva de la regla vieja —la que
 * solo mira la física de la cadencia— y por eso fallaba justo en el caso real:
 * con la cadencia de sobra y el umbral por encima de todo lo observado, el
 * bloque del puntaje declaraba dos componentes «sin medición» y este párrafo,
 * tres tarjetas más abajo, afirmaba lo contrario del mismo período.
 *
 * Un informe que se contradice no deja al lector con media verdad: le quita
 * crédito también a la mitad que tiene razón.
 *
 * La decisión vive aquí, y no en la pantalla, porque es la misma pregunta que
 * contesta el resto del módulo. Escrita en el `.tsx` volvería a ser una segunda
 * copia de la regla, y esta ya demostró que divergen.
 */
export function explainNoEpisodes(families: FamilyMeasurability[]): string {
  const unmeasured = families.filter((family) => !family.measured);

  if (!unmeasured.length) {
    const byRegime = families.filter((family) => family.reason === "within_regime_range").length;
    if (byRegime) {
      // No se puede decir que las cuatro «tuvieron oportunidad»: en las que se
      // midieron por la referencia de la cadencia ningún par llegó al umbral.
      return (
        `No se consolidó ningún episodio en el período, y las ${families.length} familias se pudieron ` +
        `medir: en ${byRegime} de ellas este vehículo no llegó al umbral común de su cadencia, que la ` +
        `mayoría de la cadencia sí alcanza, así que ese cero también es una medición. La nota de cada ` +
        `una está junto al puntaje.`
      );
    }
    return (
      `No se consolidó ningún episodio en el período. Las ${families.length} familias tuvieron ` +
      `oportunidad de dispararse con estos datos, así que ese cero es una medición: no ocurrió.`
    );
  }

  // Sin nombrar la causa: son por familia y aquí pueden convivir las dos. Se
  // remite al aviso del encabezado, que es donde `explainUnmeasured` escribe el
  // motivo de cada una — un solo lugar en la pantalla, como en el informe.
  return (
    `No se consolidó ningún episodio en el período, y ${unmeasured.length} de ${families.length} ` +
    `familias no se pudieron medir: en esas, el cero significa «no se pudo medir», no «no ` +
    `ocurrió». El motivo de cada una está en el aviso del encabezado, y no siempre es la ` +
    `cadencia de reporte.`
  );
}
